import React, { useState, useEffect, useRef, useCallback } from 'react';
import ArticleDetailsArticleCard from './AuthorDetailsArticleCard';
import { getLatesPostsForAuthor, getUserSubscribedAuthors } from '../../newsletter/services/NewsletterService';
import { Article } from '../../articles/types/Article';
import { useTranslations } from '../../localization/useTranslations';
import AuthorAndTopicCard from './AuthorAndTopicCard';
import useClickOutside from '../../hooks/useClickOutsideHandler';

interface AuthorDetailsDialogContentProps {
  id: string;
  authorName: string | undefined;
  authorImageUrl: string | undefined;
  authorTitle: string | undefined;
  authorDescription: string | undefined;
  authorLink: string | undefined;
  isFollowed: boolean;
  onFollowClick: (id: number, type: 'author' | 'topic') => void;
  onClose: () => void;
  shortDescription?: string;
  hasSocialLinks?: boolean;
  email?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  isFollowable?: boolean;
}

export default function AuthorDetailsDialogContent({
  id,
  authorName,
  authorImageUrl,
  authorTitle,
  authorDescription,
  authorLink,
  isFollowed,
  onFollowClick,
  onClose,
  shortDescription,
  hasSocialLinks,
  email,
  twitter,
  instagram,
  linkedin,
  isFollowable,
}: AuthorDetailsDialogContentProps) {
  const [latestAuthorArticles, setLatestAuthorArticles] = useState<Article[]>([]);
  const [subscribedAuthors, setSubscribedAuthors] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const translations = useTranslations();

  const getLatestAuthorArticles = useCallback(async () => {
    const subscribedAuthorsResponse = await getUserSubscribedAuthors();
    const latestAuthorPostResponse = await getLatesPostsForAuthor(parseInt(id));

    if (subscribedAuthorsResponse && latestAuthorPostResponse) {
      const subscribedAuthorIds = subscribedAuthorsResponse?.bookmarks
        ? Object.keys(subscribedAuthorsResponse?.bookmarks).map(Number)
        : [];

      const filteredPosts = latestAuthorPostResponse.filter((post: any) => {
        return !post.categories?.includes(window.printCategoryId) && !post.stitek?.includes(window.printCategoryId);
      });

      setSubscribedAuthors(subscribedAuthorIds);
      setLatestAuthorArticles(filteredPosts.slice(0, 3));
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getLatestAuthorArticles();
  }, [getLatestAuthorArticles]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useClickOutside(dialogRef, onClose);

  return (
    <div className="author-details-modal modal modal--large" ref={dialogRef}>
      <div className="author-details-modal__wrapper modal__wrapper">
        <div className="author-details-modal__header modal__header">
          <AuthorAndTopicCard
            type="author"
            name={authorName}
            title={authorTitle || ''}
            imageUrl={authorImageUrl}
            link={authorLink}
            isFollowable={isFollowable}
            id={id}
            description={authorDescription}
            shortDescription={shortDescription}
            isFollowed={isFollowed}
            hasSocialLinks
            email={email}
            twitter={twitter}
            instagram={instagram}
            linkedin={linkedin}
            onFollowClick={onFollowClick}
            layout={'in-modal'}
            nameType="div" // Since modal is already open, we dont want the name to be clickable
          />
          <button className="close-button author-details-modal__close-button" onClick={onClose}></button>
        </div>
        <div className="author-details-modal__content modal__content">
          <h4 className="heading">{translations?.authorAndTopic?.latestArticle || 'Latest article'}</h4>
          {isLoading ? (
            <div style={{ height: '200px' }} />
          ) : (
            <div className="author-details-modal__posts-wrapper">
              {latestAuthorArticles.length > 0 ? (
                latestAuthorArticles.map((article, index) => (
                  <ArticleDetailsArticleCard
                    key={index}
                    tagLink={article.tagLink}
                    tagTitle={article.tagName}
                    articleId={article.id}
                    articleLink={article.link}
                    articleTitle={article.title}
                    articlePublishDate={article.publishDate}
                    articleReadingTime={article.readTime}
                    articleImage={article.imageLink}
                    isBookmarked={subscribedAuthors.includes(parseInt(article.id))}
                    bookmarkIds={subscribedAuthors}
                  />
                ))
              ) : isLoading ? (
                <p>{/* Note: Loading state component */}</p>
              ) : (
                <div className="empty-state">
                  <p className="empty-message">
                    {authorName}{' '}
                    {translations?.emptyStates?.noPublishedArticles || 'You have not published any articles yet'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="author-details-modal__footer modal__footer">
          <a href={authorLink} rel="noopener" className="author-details-modal__link">
            {translations?.authorAndTopic?.authorDetailsButton || 'View author details'}
          </a>
        </div>
      </div>
    </div>
  );
}
